import type { ReactElement } from "react";

import type { IconName } from "../../design-system/icons";
import { Icon } from "../atoms/Icon";
import styles from "./SegmentedControl.module.css";

export interface SegmentOption<V extends string> {
  readonly value: V;
  /** Visible text, or the accessible name of an icon-only segment (from `t()`). */
  readonly label: string;
  readonly icon?: IconName | undefined;
  /**
   * A fuller accessible name for a short visible label (the mockup's "8" read as "Mediano, 8 px"). It
   * must contain the visible text (WCAG 2.5.3); without it the label is the name.
   */
  readonly name?: string | undefined;
}

export interface SegmentedControlProps<V extends string> {
  /** The accessible name of the group (from `t()`). */
  readonly label: string;
  readonly options: readonly SegmentOption<V>[];
  readonly value: V;
  readonly onChange: (value: V) => void;
  /** Draw only the icons (the mockup's `.seg-icon`); every segment then needs an `icon`. */
  readonly iconOnly?: boolean | undefined;
  /** Every segment is disabled (e.g. while a turn is open). */
  readonly disabled?: boolean | undefined;
  /** The ids of a hint or a refusal that describe the group (inside a form field). */
  readonly describedBy?: string | undefined;
}

/**
 * Two to five mutually exclusive views (the mockup's `.seg`): a named group of toggle buttons with
 * exactly one `aria-pressed="true"`. Each segment is its own tab stop, as in the mockup; content that
 * changes below with the choice belongs to `Tabs` instead.
 */
export function SegmentedControl<V extends string>({
  label,
  options,
  value,
  onChange,
  iconOnly = false,
  disabled = false,
  describedBy,
}: SegmentedControlProps<V>): ReactElement {
  return (
    <div className={[styles.group, iconOnly ? styles.iconOnly : undefined].filter(Boolean).join(" ")} role="group" aria-label={label} aria-describedby={describedBy}>
      {options.map((option) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={styles.segment}
            aria-pressed={pressed}
            disabled={disabled}
            aria-label={iconOnly ? option.label : option.name}
            onClick={() => {
              if (!pressed) onChange(option.value);
            }}
          >
            {option.icon === undefined ? null : <Icon name={option.icon} size="sm" />}
            {iconOnly ? null : option.label}
          </button>
        );
      })}
    </div>
  );
}
